import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import type {
  UserReferralCode,
  ReferralStats,
  ReferralWithDetails,
  ReferralEarning,
} from "../types";

import { API_BASE_URL as API_BASE } from "@/backend/config";

// Query keys
const REFERRAL_CODE_KEY = "referral-code";
const REFERRAL_STATS_KEY = "referral-stats";
const MY_REFERRALS_KEY = "my-referrals";
const REFERRAL_EARNINGS_KEY = "referral-earnings";

// ============================================================================
// Hooks
// ============================================================================

/**
 * Get or create user's referral code
 */
export function useMyReferralCode() {
  const { userId } = useUnifiedAuth();

  return useQuery({
    queryKey: [REFERRAL_CODE_KEY, userId],
    queryFn: async (): Promise<UserReferralCode | null> => {
      if (!userId) return null;

      const response = await fetch(
        `${API_BASE}/api/user-referrals/my-code?user_id=${userId}`
      );
      if (!response.ok) throw new Error("Failed to fetch referral code");

      const data = await response.json();
      return data.code;
    },
    enabled: !!userId,
  });
}

/**
 * Update custom referral code
 */
export function useUpdateReferralCode() {
  const { userId } = useUnifiedAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customCode: string | null) => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/my-code?user_id=${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ custom_code: customCode }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to update referral code");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CODE_KEY, userId] });
    },
  });
}

/**
 * Get referral stats for dashboard
 */
export function useReferralStats() {
  const { userId } = useUnifiedAuth();

  return useQuery({
    queryKey: [REFERRAL_STATS_KEY, userId],
    queryFn: async (): Promise<ReferralStats> => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/stats?user_id=${userId}`
      );
      if (!response.ok) throw new Error("Failed to fetch referral stats");

      const data = await response.json();
      return data.stats;
    },
    enabled: !!userId,
  });
}

/**
 * Get list of users referred
 */
export function useMyReferrals() {
  const { userId } = useUnifiedAuth();

  return useQuery({
    queryKey: [MY_REFERRALS_KEY, userId],
    queryFn: async (): Promise<ReferralWithDetails[]> => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/my-referrals?user_id=${userId}`
      );
      if (!response.ok) throw new Error("Failed to fetch referrals");

      const data = await response.json();
      return data.referrals;
    },
    enabled: !!userId,
  });
}

/**
 * Get earnings history
 */
export function useReferralEarnings() {
  const { userId } = useUnifiedAuth();

  return useQuery({
    queryKey: [REFERRAL_EARNINGS_KEY, userId],
    queryFn: async (): Promise<ReferralEarning[]> => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/earnings?user_id=${userId}`
      );
      if (!response.ok) throw new Error("Failed to fetch earnings");

      const data = await response.json();
      return data.earnings;
    },
    enabled: !!userId,
  });
}

/**
 * Claim referral earnings to trading balance
 */
export function useClaimReferralEarnings() {
  const { userId } = useUnifiedAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (amount: string) => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/claim?user_id=${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to claim earnings");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [REFERRAL_STATS_KEY, userId] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_EARNINGS_KEY, userId] });
      // Also invalidate account balance
      queryClient.invalidateQueries({ queryKey: ["account", userId] });
    },
  });
}

/**
 * Link a referral code to current user (called on first login)
 */
export function useLinkReferralCode() {
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (refCode: string) => {
      if (!userId) throw new Error("Not authenticated");

      const response = await fetch(
        `${API_BASE}/api/user-referrals/link?user_id=${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ref_code: refCode }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to link referral code");
      }

      return response.json();
    },
  });
}

/**
 * Combined hook that provides all referral data
 */
export function useReferralData() {
  const { data: code, isLoading: isLoadingCode } = useMyReferralCode();
  const { data: stats, isLoading: isLoadingStats } = useReferralStats();
  const { data: referrals = [], isLoading: isLoadingReferrals } = useMyReferrals();
  const { data: earnings = [], isLoading: isLoadingEarnings } = useReferralEarnings();

  return {
    code,
    stats,
    referrals,
    earnings,
    isLoading: isLoadingCode || isLoadingStats || isLoadingReferrals || isLoadingEarnings,
    isLoadingCode,
    isLoadingStats,
    isLoadingReferrals,
    isLoadingEarnings,
  };
}

